// Import all dependencies first (ESLint requires imports at top)
import { doc, getFirestore, runTransaction, getDocs, collection, deleteField } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { percentage, statusDotColours } from '../styles/GlobalStyles';

// Import functions from modules for internal use
import { setSubData, setData } from './firestore';
import { callFirebaseFunction, cloudFunctionV2 } from './api';
import { setMixpanelAlias, recordEvent, aliasMixpanel } from './analytics';

// Re-export from modular functions for better organization and tree-shaking
export * from './auth';
export * from './api';
export * from './analytics';
export * from './firestore';
export * from './comps';
export * from './property';
export * from './arv';
export * from './utils';
export * from './timeline';
export * from './rendering';
export * from './geoUtils';

// Removed duplicate functions - now exported from './arv', './utils', './comps', and './property' modules above


// Note: setData, setSubData, setDataRandomId, getDocument, setTransaction, etc. are now exported from './firestore' module above
// Removed duplicate definitions - now using module versions exported above via 'export * from './firestore''

export const setHiddenPropertyTransaction = async (colRef, docRef, subColRef, subDocRef, hiddenProperty) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				
				if ( data.hiddenProperties === undefined ) {
					data.hiddenProperties = [hiddenProperty];
				}
				else {
					const hiddenPropertiesArray = data.hiddenProperties;
					const index = hiddenPropertiesArray.findIndex(e => e.zpid === hiddenProperty.zpid)
					if ( index === -1 ) {
						hiddenPropertiesArray.push(hiddenProperty);
					}
					else {
						hiddenPropertiesArray.splice(index, 1);
					}
					data.hiddenProperties = hiddenPropertiesArray;
				}

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
}

export const setDefaultPropertyInfo = async (colRef, docRef, subColRef, subDocRef, selectedComps, arvData, arvMessages) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				
				data.selectedComps = selectedComps;
				data.arvData = arvData;
				data.arvMessages = arvMessages;

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const setSelectedPropertyTransaction = async (colRef, docRef, subColRef, subDocRef, selectedComps) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				
				data.selectedComps = selectedComps;

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

// Atomic toggle for a single zpid in a role-scoped user-kept array (e.g.
// `data.userArvZpids` or `data.userRentReadyZpids`). Reads the current value
// inside the transaction so back-to-back clicks (before a Firestore listener
// round-trip) don't race on a stale read. `seedZpids` is the agent's pick set
// for that role, used to lazy-seed the field on the user's first edit so the
// starting point matches the displayed "kept" state. Returns the resulting
// array so callers can log / debug.
export const toggleUserKeptZpidTransaction = async (colRef, docRef, subColRef, subDocRef, fieldName, zpid, seedZpids) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	const zpidStr = String(zpid);
	const fieldPath = `data.${fieldName}`;
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
				return;
			}
			const data = query.data()?.data || {};
			const current = data[fieldName];
			const baseline = Array.isArray(current)
				? current.map(String)
				: (seedZpids || []).map(String);
			const next = baseline.includes(zpidStr)
				? baseline.filter(z => z !== zpidStr)
				: [...baseline, zpidStr];
			transaction.update(queryParams, {
				[fieldPath]: next
			});
			res = {
				status: 200,
				message: "Transaction successfully committed",
				[fieldName]: next
			};
		});

		return res;
	}
	catch (e) {
		return {
			status: 400,
			message: `Transaction error: ${e}`
		};
	}
};

// User-curated comp overlay for a specific role (ARV or rent-ready). `nextZpids`
// is either a string[] of zpids the user wants in that role, or null to clear
// the field (revert to the agent's picks for that role). Each role lives in
// its own field (e.g. `data.userArvZpids`) so user edits to one don't bleed
// into the other and a Reset on one role doesn't touch the other.
export const setUserKeptZpidsTransaction = async (colRef, docRef, subColRef, subDocRef, fieldName, nextZpids) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	const fieldPath = `data.${fieldName}`;
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
				return;
			}
			if (nextZpids === null) {
				transaction.update(queryParams, {
					[fieldPath]: deleteField()
				});
			}
			else {
				transaction.update(queryParams, {
					[fieldPath]: nextZpids
				});
			}
			res = {
				status: 200,
				message: "Transaction successfully committed"
			};
		});

		return res;
	}
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const setSelectedPropertyReportTransaction = async (colRef, docRef, subColRef, subDocRef, selectedComps) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};

				res = await setSubData(colRef, docRef, subColRef, subDocRef, {selectedComps: selectedComps})
			}
			else {
				let data = query.data().data;
				
				data.selectedComps = selectedComps;

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const setBasementTransaction = async (colRef, docRef, subColRef, subDocRef, basement, basementSqFootage) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				
				// Set basement boolean
				data.basement = Boolean(basement);
				
				// Set basementSqFootage based on basement value
				if (basement === true && basementSqFootage !== undefined && basementSqFootage !== null) {
					const sqft = Number(basementSqFootage);
					if (!isNaN(sqft) && sqft >= 0) {
						data.basementSqFootage = sqft;
					}
				} else if (basement === false) {
					// Always set to 0 when basement is false
					data.basementSqFootage = 0;
				}

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const setPropertyDescriptionsTransaction = async (colRef, docRef, subColRef, subDocRef, descriptions) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				
				data.propertyDescriptions = descriptions;

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const setPropertyAsIsData = async (colRef, docRef, subColRef, subDocRef, asIsData) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				
				data.asIsData = asIsData;

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const getDocumentNames = async (colRef) => {
    const db = getFirestore();
    const queryRef = collection(db, colRef);

    return new Promise(async (resolve) => {
        await getDocs(queryRef)
            .then((querySnapshot) => {
                const docNames = querySnapshot.docs.map((doc) => doc.id);
                const res = {
                    status: 200,
                    data: docNames,
                    message: "Documents retrieved successfully!"
                };
                resolve(res);
            })
            .catch((error) => {
                const res = {
                    status: 400,
                    data: [],
                    message: `Error retrieving document names: ${error}`,
                    code: error.code
                };
                resolve(res);
            });
    });
};

// Removed duplicate functions - now exported from './firestore' module above:
// getDocument, getSubDocument, getSubSubDocument, getAllDocuments, getAllSubDocuments, getWhereDoc

export const uploadImage = async(file, code, setLoading) => {
	const storage = getStorage();
	const storageRef = ref(storage, code);
	let blob;
	// If the caller passed a File/Blob (e.g. from an <input type="file">) use it directly.
	if (file instanceof Blob) {
		blob = file;
	} else {
		// Otherwise treat "file" as a URL/Data‑URI and fetch it.
		const response = await fetch(file);
		if (!response.ok) {
			throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
		}
		blob = await response.blob();
	}
	// Derive a sane MIME type – sometimes fetch() returns "text/html" for a failed request
	let mime = blob.type;
	if (!mime || mime.startsWith('text/')) {
		// Fallback to extension‑based guess
		const ext = code.split('.').pop().toLowerCase();
		if (ext === 'png')      mime = 'image/png';
		else if (ext === 'jpg' || ext === 'jpeg') mime = 'image/jpeg';
		else if (ext === 'gif') mime = 'image/gif';
		else                    mime = 'application/octet-stream';
	}
	const metadata = { contentType: mime };

	const uploadTask = uploadBytesResumable(storageRef, blob, metadata);

	return new Promise(resolve => {
		uploadTask.on('state_changed',
			(snapshot) => {
				// Observe state change events such as progress, pause, and resume
				// Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
				const progress = (snapshot.bytesTransferred / snapshot.totalBytes);
				setLoading(percentage.format(progress));
				console.log('Upload is ' + progress + '% done');
				switch (snapshot.state) {
				case 'paused':
					console.log('Upload is paused');
					break;
				case 'running':
					console.log('Upload is running');
					break;
				default:
					console.log("Upload default case");
				}
			},
			(error) => {
				console.log("Error = ", error);
				const res = {
					status: 400,
					message: error.message,
					code: error.code
				};
				resolve(res);
			},
			() => {
				getDownloadURL(uploadTask.snapshot.ref)
				.then(async(downloadURL) => {
					setLoading(null);
					const res = {
						status: 200,
						url: downloadURL
					};
					resolve(res);
				});
			}
		);
	})
}

// Removed duplicate functions - now exported from './analytics' module above:
// resetMixpanel, setMixpanelAlias, recordEvent, mixpanelSetup


// recordEvent is now exported from './analytics' module above

export const generateEmptyEmailVerification = async(userId) => {
	const colRef = "email_verification";
	const data = [];

	return new Promise(async resolve => {
		const writeData = await setData(colRef, userId, data);
		resolve(writeData);
	});
};

export const propertyGatewayString = (prefix, params) => {
	const queryString = serializeParams(params);
	const pathWithQueryString = `${prefix}${queryString}`;
	return pathWithQueryString;
};

export const propertyGatewayStringReRun = (prefix, params) => {
	const queryString = serializeParams(params);
	const pathWithQueryString = `${prefix}${queryString}&forceRun=true`;
	return pathWithQueryString;
};

export const missingCompsString = (prefix, params) => {
	const queryString = `propertyId=${params.propertyId}&urls=${JSON.stringify(params.urls)}`;
	const pathWithQueryString = `${prefix}${queryString}`;
	return pathWithQueryString;
};

const serializeParams = (obj, prefix = '') => {
	const queryParts = [];
	for (const key in obj) {
		if (obj.hasOwnProperty(key)) {
			const value = obj[key];
			const prefixedKey = prefix ? `${prefix}.${key}` : key;

			if (typeof value === 'object' && !Array.isArray(value)) {
				// Recursively serialize nested objects
				queryParts.push(serializeParams(value, prefixedKey));
			} else {
				// Encode key and value, remove string quotations for non-nested values
				const encodedValue = encodeURIComponent(value);
				queryParts.push(`${prefixedKey}=${encodedValue}`);
			}
		}
	}
	return queryParts.join('&');
};

// Removed duplicate functions - now exported from './comps' and './property' modules above


// mixpanelSetup is now exported from './analytics' module above
// regenerateTimeline and generateNovationScores are now exported from './timeline' module above
// renderHTML, renderHTMLAdjustments, mergeAdjustmentAbstracts, and wireZpidClicks are now exported from './rendering' module above

export const getFlipDetails = (property) => {
	const flipStatus = property.compDetails.flop === true ? "Flop" : property.compDetails.flip === true ? "Flip" : "Non-flip";
	const flipBGColour = flipStatus === "Flop" ? "bg-colour-flop" : flipStatus === "Flip" ? "bg-colour-flip" : "bg-colour-non-flip";
	const flipBGColourDark = flipStatus === "Flop" ? "bg-colour-flop-dark" : flipStatus === "Flip" ? "bg-colour-flip-dark" : "bg-colour-non-flip-dark";
	const flippingDescription = property.listingDetails.listingStatus === "for_sale" || property.listingDetails.listingStatus === "pending" ? "Property is being flipped, specifically remodeled for re-sale." : "Property was flipped, specifically remodeled for re-sale."
	const flipDescription = flipStatus === "Flop" ? "Property is a flip, but is struggling to sell. On market for over 90 days." : flipStatus === "Flip" ? flippingDescription : "Excellent condition comp, but not remodeled specifically for re-sale.";
	const flipDotcolour = flipStatus === "Flop" ? statusDotColours.flop : flipStatus === "Flip" ? statusDotColours.flip : statusDotColours.nonFlip;
	return { flipStatus, flipBGColour, flipDescription, flipDotcolour, flipBGColourDark };
};

// Minimal HTML escaper for safe inline injection

// Replace tokens like [zpid_12345678] (optionally wrapped in parentheses) with street addresses.
// Options:
//   - clickable (default: true): when false, renders plain text (or a <span>) instead of an <A> tag.
//   - underline (default: true): when false, removes the underline decoration.
//   - className (default: "colour-primary"): extra class to add to the clickable link or span wrapper.




// Attach a delegated click handler for any generated address links

export const getNames = (words) => {
	const n = (words || "").split(" ").filter(Boolean);
	const object = {
		firstName: n[0] || "",
		lastName: n[n.length - 1] || ""
	}
	return object;
}

export const createSignupWorkspace = async(payload) => {
	return callFirebaseFunction('createSignupWorkspace', payload);
};

export const completeSignUp = async(
	signUpUser, 
	googleAuth,
	email,
	firstName,
	lastName
) => {
	const docRef = signUpUser.user.uid;
	const displayName = googleAuth === false ? `${firstName} ${lastName}` : signUpUser.user.displayName;
	const displayEmail = googleAuth === false ? email : signUpUser.user.email;
	const names = googleAuth === false ? null : getNames(displayName);
	setMixpanelAlias(docRef);

	const profile = {
		email: googleAuth === false ? email : displayEmail,
		firstName: googleAuth === false ? firstName : names.firstName,
		lastName: googleAuth === false ? lastName : names.lastName,
		displayName
	};

	const result = await createSignupWorkspace(profile);
	if ( result?.status === 200 ) {
		if (result.profileCreated === true) {
			recordEvent("account_created", {
				userId: docRef,
				email: googleAuth === false ? email : signUpUser.user.email,
			});

			// Merge the pre-auth trial identity (Mixpanel distinct_id = the email
			// they forwarded deals from) into this new user, so the
			// trial→signup→paid funnel resolves to one person. Once-only
			// (profileCreated guard). Best-effort: only stitches when the signup
			// email matches the forwarded email.
			aliasMixpanel(docRef, displayEmail);

			if ( googleAuth === true ) {
				recordEvent("email_verified", {});
			}

			// Mailchimp signup subscription disabled for now
			// const mailchimpURL = process.env.REACT_APP_MAILCHIMP_API;
			// const mailchimpObject = {
			// 	list_id: "2eac986d19",
			// 	get: false,
			// 	values: {
			// 		email_address: profile.email,
			// 		status: "subscribed",
			// 		merge_fields: {
			// 			FNAME: profile.firstName,
			// 			LNAME: profile.lastName,
			// 			PHONE: ""
			// 		}
			//
			// 	}
			// }
			// cloudFunctionV2(mailchimpURL, mailchimpObject);
		}
	}

	return result;
};

export const queryATTOM = async(streetAddress, city, state, zipcode) => {
	return new Promise(async resolve => {
		const url = process.env.REACT_APP_ATTOM_QUERY;
		const params = {
			address1: streetAddress,
			address2: `${city}, ${state} ${zipcode}`
		};
		const queryData = await cloudFunctionV2(url, params);
		resolve(queryData);
	});
};

export const getExclusionDescription = (item, subjectProperty, comp) => {
	const subjectPropertyConstructionType = subjectProperty.propertyAttributes.constructionTypeBySource === undefined || subjectProperty.propertyAttributes.constructionTypeBySource === null || subjectProperty.propertyAttributes.constructionTypeBySource.reapi === null ? subjectProperty.propertyAttributes.constructionType : subjectProperty.propertyAttributes.constructionTypeBySource.reapi;
	const constructionType = comp.propertyAttributes.constructionTypeBySource === undefined || comp.propertyAttributes.constructionTypeBySource === null || comp.propertyAttributes.constructionTypeBySource.reapi === null ? comp.propertyAttributes.constructionType : comp.propertyAttributes.constructionTypeBySource.reapi;

	const subjectPool = subjectProperty.propertyAttributes.pool;
	const compPool = comp.propertyAttributes.pool;

	const subjectBeds = subjectProperty.propertyAttributes.bedrooms;
	const compBeds = comp.propertyAttributes.bedrooms;

	const subjectBaths = subjectProperty.propertyAttributes.bathrooms;
	const compBaths = comp.propertyAttributes.bathrooms;

	const subjectStories = subjectProperty.propertyAttributes.stories;
	const compStories = comp.propertyAttributes.stories;

	const compADU = comp.propertyAttributes.adu;

	const descriptions = {
		exclude_rules: `**Disqualifier -** This comp was deemed to be an outlier because of its characteristics`,
		low_similarity: `**Disqualifier -** This comp was too dissimilar to the subject property`,
		outlier: `**Disqualifier -** This comp was deemed to be an outlier because of its characteristics`,
		user_excluded: `**Disqualifier -** This comp was excluded by you when you chose your price ceiling`,
		unconventional_market: `**Disqualifier -** This comp had unconventional market conditions`,
		construction_material: `**Disqualifier -** This comp has a ${constructionType} construction material whereas the subject property has a ${subjectPropertyConstructionType} construction material`,
		pool_filter: `**Disqualifier -** This comp ${compPool === true ? "has" : "does not have"} a pool whereas the subject property ${subjectPool === true ? "has" : "does not have"} a pool`,
		bed_bath_filter: `**Disqualifier -** This comp is a ${compBeds} bed ${compBaths} bath whereas the subject property is a ${subjectBeds} bed ${subjectBaths} bath property`,
		stories_filter: `**Disqualifier -** This comp is a ${compStories} story property whereas the subject property is a ${subjectStories} story property`,
		adu_filter: `**Disqualifier -** ${compADU === false ? "The subject property has an ADU whereas the comp does not" : "The subject property does not have an ADU whereas the comp does"}`,
		arv_comp_selection: `**Disqualifier -** This comp was excluded at the final ARV comp selection stage`
	}

	const reason = item.reason;
	const description = descriptions[reason];

	if ( item.description ) {
		return item.description;
	}
	return description;
};

export const formatPhoneNumber = (phoneNumber) => {
    // Remove all non-numeric characters
    let cleaned = ('' + phoneNumber).replace(/\D/g, '');

    // Remove leading '1' if present and length is 11
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
        cleaned = cleaned.substring(1);
    }

    // Truncate to maximum 10 digits
    cleaned = cleaned.slice(0, 10);

    const len = cleaned.length;

    if (len === 0) return '';
    if (len < 4) return `(${cleaned}`;
    if (len < 7) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
};

export const cleanPhoneNumber = (phoneNumber) => {
    // Remove all non-numeric characters
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');

    return cleaned;
};

export const isValidURL = (value) => {
	try {
		const url = new URL(value);
		return url.protocol === "http:" || url.protocol === "https:";
	} 
	catch (_) {
		return false;
	}
}

export const startPreAgenticFlow = (
	zipcode,
	address,
	latitude,
	longitude,
	propertyId,
	companyId,
	constructionType,
	yearBuilt,
	stories,
	floodZone,
	state,
	largeLot,
	countyId,
	forceRefresh = false,
	city = null,
	propertyType = null
) => {
	// Detect if running locally (frontend on localhost)
	const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
	const projectId = 'clozers';
	const region = 'us-central1';
	
	// Use local emulator URL if running locally, otherwise use environment variable
	const emulatorUrl = `http://localhost:5001/${projectId}/${region}/preAgenticStep`;
	const url = isLocalhost ? emulatorUrl : process.env.REACT_APP_PRE_AGENTIC;
	const params = {
		companyId: companyId,
		propertyId: `${propertyId}`,
		zipcode: zipcode,
		address: address,
		latitude: latitude,
		longitude: longitude,
		imageAnalysisModel: "gpt-5-mini",
		constructionType: constructionType,
		yearBuilt: yearBuilt,
		stories: stories,
		floodZone: floodZone === undefined || floodZone === null ? "D" : floodZone,
		state: state,
		largeLot: largeLot,
		countyId: countyId,
		forceRefresh: forceRefresh,
		city: city,
		propertyType: propertyType
	};
	return new Promise(async resolve => {
		let queryData = await cloudFunctionV2(url, params);

		// If running on localhost and emulator call failed, try production as fallback
		if (isLocalhost && queryData.status !== 200 && url === emulatorUrl) {
			console.warn('[startPreAgenticFlow] Emulator call failed, falling back to production URL');
			const productionUrl = process.env.REACT_APP_PRE_AGENTIC;
			queryData = await cloudFunctionV2(productionUrl, params);
		}
		if ( queryData.status === 200 ) {
			const res = {
				status: 200,
				data: queryData
			}
			resolve(res);
		}
		else {
			const res = {
				status: 400,
				error: queryData.error
			};
			resolve(res);
		}
	});
};
